import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  formatInteger,
  type ExternalProperty,
  type IntegrationConnection,
  type PropertySelectionMode,
} from '@brimo/shared';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Pill,
  Segmented,
  Skeleton,
  Table,
  Toggle,
  useToast,
  type TableColumn,
} from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  useExternalProperties,
  useImportRuns,
  useIntegrationConnections,
  useSaveSelection,
  useStartRun,
  useUpdateConnection,
} from './api';
import {
  dateTimeLabel,
  durationLabel,
  includedIds,
  isRunActive,
  progressLine,
  runStatusTones,
  selectionChanged,
  selectionFor,
  startedByLabel,
  totalsLine,
} from './report';
import styles from './IntegrationsPage.module.css';

const INTRO =
  'Externe Systeme, aus denen DAMOKLES Objekte, Einheiten, Unterlagen, Bilder und Mietverträge übernimmt.';
const IMPORT_ONLY_NOTE =
  'DAMOKLES liest Objekte aus TeamProQ, schreibt aber nichts zurück. Die Umstellung auf beidseitigen Abgleich erfolgt erst nach Freigabe.';
const APPLY_CONFIRM =
  'Der Import legt Objekte, Einheiten, Dokumente und Bilder in DAMOKLES an oder aktualisiert sie. In TeamProQ wird nichts verändert.';

/**
 * Einstellungen › Organisation › „Integrationen" (no design screen, D-189): the TeamProQ connection, its
 * property selection, dry run / import and the run history. The connection itself is set up on the server
 * (CLI); the write direction has no control here.
 */
export function IntegrationsPage() {
  const connections = useIntegrationConnections();
  const items = connections.data?.items ?? [];
  return (
    <OrganizationPage title="Integrationen" description={INTRO}>
      {connections.isError ? <QueryError error={connections.error} /> : null}
      {connections.isPending ? (
        <div className={styles.stack} aria-busy="true">
          <Skeleton variant="block" height={180} />
          <Skeleton variant="block" height={320} />
        </div>
      ) : null}
      {connections.isSuccess && items.length === 0 ? (
        <EmptyState
          eyebrow="TeamProQ"
          title="Noch keine Verbindung eingerichtet."
          description="Die Einrichtung erfolgt durch den Administrator auf dem Server."
        />
      ) : null}
      {items.map((connection) => (
        <ConnectionSection key={connection.id} connection={connection} />
      ))}
    </OrganizationPage>
  );
}

function ConnectionSection({ connection }: { connection: IntegrationConnection }) {
  return (
    <div className={styles.stack}>
      <ConnectionCard connection={connection} />
      <SelectionCard connection={connection} />
      <RunsCard connection={connection} />
    </div>
  );
}

function ConnectionCard({ connection }: { connection: IntegrationConnection }) {
  const { toast } = useToast();
  const update = useUpdateConnection(connection.id);
  const start = useStartRun(connection.id);
  const [confirmApply, setConfirmApply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = `integration-${connection.id}`;
  const running = connection.activeRunId !== null;
  const progress = connection.lastRun && isRunActive(connection.lastRun.status) ? connection.lastRun : null;

  function toggleAutoSync(autoSync: boolean) {
    setError(null);
    update.mutate(
      { autoSync },
      {
        onSuccess: () =>
          toast({
            message: autoSync
              ? 'Automatischer Abgleich ist eingeschaltet.'
              : 'Automatischer Abgleich ist ausgeschaltet.',
          }),
        onError: (failure) => setError(errorMessage(failure)),
      },
    );
  }

  function run(mode: 'dry_run' | 'apply') {
    setError(null);
    start.mutate(mode, {
      onSuccess: () => {
        setConfirmApply(false);
        toast({ message: mode === 'apply' ? 'Import gestartet.' : 'Probelauf gestartet.' });
      },
      onError: (failure) => {
        setConfirmApply(false);
        setError(errorMessage(failure));
      },
    });
  }

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitleBlock}>
          <span className={styles.eyebrow}>{connection.sourceLabel}</span>
          <h3 id={titleId} className={styles.cardTitle}>
            {connection.name}
          </h3>
        </div>
        <Pill variant={connection.mode === 'import_only' ? 'info' : 'warning'}>
          {connection.mode === 'import_only' ? 'Nur Import (Testphase)' : connection.modeLabel}
        </Pill>
      </div>
      {connection.mode === 'import_only' ? <p className={styles.note}>{IMPORT_ONLY_NOTE}</p> : null}
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Verbindung</dt>
          <dd>
            {connection.connectionKey}
            <span className={styles.factMeta}>{connection.host}</span>
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Status</dt>
          <dd>
            {connection.active ? 'Verbunden' : 'Pausiert'}
            <span className={styles.factMeta}>
              {connection.credentialsVerifiedAt
                ? `Zugriff geprüft am ${dateTimeLabel(connection.credentialsVerifiedAt)}`
                : 'Zugriff noch nicht geprüft'}
            </span>
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Letzter Lauf</dt>
          <dd>
            {connection.lastRun
              ? `${connection.lastRun.modeLabel} · ${connection.lastRun.statusLabel}`
              : 'Noch kein Lauf'}
            <span className={styles.factMeta}>
              {connection.lastRun
                ? `${dateTimeLabel(connection.lastRun.createdAt)} · ${totalsLine(connection.lastRun.totals)}`
                : '—'}
            </span>
          </dd>
        </div>
      </dl>
      <div className={styles.toggleRow}>
        <Toggle
          label="Automatischer Abgleich (stündlich)"
          checked={connection.autoSync}
          disabled={update.isPending || !connection.active}
          onChange={toggleAutoSync}
        />
        <span className={styles.toggleHint}>
          Führt jede Stunde einen Import mit der gespeicherten Objektauswahl aus.
        </span>
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {progress ? (
        <Banner tone="info">
          {progress.modeLabel} läuft
          {progressLine(progress.progress) ? ` · ${progressLine(progress.progress) ?? ''}` : ''}. Die Seite
          aktualisiert sich automatisch.
        </Banner>
      ) : null}
      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => run('dry_run')}
          disabled={running || !connection.active}
          loading={start.isPending && start.variables === 'dry_run'}
        >
          Probelauf starten
        </Button>
        <Button onClick={() => setConfirmApply(true)} disabled={running || !connection.active}>
          Import ausführen
        </Button>
        <span className={styles.actionsHint}>
          Der Probelauf zeigt, was sich ändern würde, ohne etwas zu speichern.
        </span>
      </div>
      <Dialog
        open={confirmApply}
        onClose={() => setConfirmApply(false)}
        eyebrow={connection.sourceLabel}
        title="Import ausführen?"
        width={520}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmApply(false)} disabled={start.isPending}>
              Abbrechen
            </Button>
            <Button loading={start.isPending} onClick={() => run('apply')}>
              Import ausführen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>{APPLY_CONFIRM}</p>
      </Dialog>
    </section>
  );
}

function SelectionCard({ connection }: { connection: IntegrationConnection }) {
  const { toast } = useToast();
  const properties = useExternalProperties(connection.id, true);
  const save = useSaveSelection(connection.id);
  const items = useMemo(() => properties.data?.items ?? [], [properties.data]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<PropertySelectionMode>(connection.propertySelection.mode);
  const titleId = `integration-selection-${connection.id}`;

  useEffect(() => {
    setChecked(includedIds(items));
  }, [items]);
  useEffect(() => {
    setMode(connection.propertySelection.mode);
  }, [connection.propertySelection.mode]);

  const next = selectionFor(mode, items, checked);
  const dirty = properties.isSuccess && selectionChanged(connection.propertySelection, next, items);

  function toggle(id: string, on: boolean) {
    setChecked((current) => {
      const copy = new Set(current);
      if (on) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  }

  function submit() {
    save.mutate(next, {
      onSuccess: () => toast({ message: 'Objektauswahl gespeichert.' }),
      onError: (failure) => toast({ message: errorMessage(failure) }),
    });
  }

  const columns: TableColumn<ExternalProperty>[] = [
    {
      id: 'include',
      header: <span className={styles.srOnly}>Importieren</span>,
      track: '40px',
      cell: (row) => (
        <Checkbox
          label={`${row.name} importieren`}
          labelHidden
          checked={checked.has(row.externalId)}
          onChange={(event) => toggle(row.externalId, event.target.checked)}
        />
      ),
    },
    {
      id: 'name',
      header: 'Objekt',
      track: 'minmax(0, 2fr)',
      cell: (row) => (
        <span className={styles.cellStack}>
          <span className={styles.cellStrong}>{row.name}</span>
          {row.addressLine ? <span className={styles.cellMeta}>{row.addressLine}</span> : null}
        </span>
      ),
    },
    { id: 'category', header: 'Ordner', cell: (row) => row.category ?? '—' },
    { id: 'city', header: 'Ort', cell: (row) => row.city ?? '—' },
    {
      id: 'units',
      header: 'Einheiten',
      numeric: true,
      track: '90px',
      cell: (row) => (row.unitCount === null ? '—' : formatInteger(row.unitCount)),
    },
    {
      id: 'damokles',
      header: 'In DAMOKLES',
      track: 'minmax(0, 1.2fr)',
      cell: (row) =>
        row.damoklesPropertyId ? (
          <Link className={styles.link} to={`/properties/${row.damoklesPropertyId}`}>
            Importiert · Objekt öffnen
          </Link>
        ) : (
          <span className={styles.cellMeta}>Noch nicht importiert</span>
        ),
    },
  ];

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitleBlock}>
          <span className={styles.eyebrow}>Objektauswahl</span>
          <h3 id={titleId} className={styles.cardTitle}>
            Welche Objekte übernommen werden
          </h3>
        </div>
        {properties.data ? (
          <span className={styles.headMeta}>
            Stand aus TeamProQ: {dateTimeLabel(properties.data.fetchedAt)}
          </span>
        ) : null}
      </div>
      <div className={styles.modeRow}>
        <span className={styles.modeLabel}>Neue Objekte in TeamProQ</span>
        <Segmented<PropertySelectionMode>
          label="Neue Objekte in TeamProQ"
          value={mode}
          items={[
            { value: 'exclude', label: 'automatisch importieren' },
            { value: 'include', label: 'erst nach Auswahl importieren' },
          ]}
          onChange={setMode}
        />
      </div>
      {properties.isError ? <QueryError error={properties.error} /> : null}
      {properties.isPending ? (
        <div className={styles.stackTight} aria-busy="true">
          <Skeleton height={14} width="60%" />
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="50%" />
        </div>
      ) : null}
      {properties.isSuccess ? (
        <Table
          label="Objekte in TeamProQ"
          columns={columns}
          rows={items}
          rowKey={(row) => row.externalId}
          density="compact"
          emptyText="TeamProQ liefert für diese Verbindung keine Objekte."
        />
      ) : null}
      <div className={styles.actions}>
        <Button onClick={submit} disabled={!dirty} loading={save.isPending}>
          Auswahl speichern
        </Button>
        <span className={styles.actionsHint}>
          {formatInteger(checked.size)} von {formatInteger(items.length)} Objekten werden importiert.
        </span>
      </div>
    </section>
  );
}

function RunsCard({ connection }: { connection: IntegrationConnection }) {
  const navigate = useNavigate();
  const runs = useImportRuns(connection.id, connection.activeRunId !== null);
  const [now, setNow] = useState(() => Date.now());
  const titleId = `integration-runs-${connection.id}`;
  const items = runs.data?.items ?? [];
  const anyActive = items.some((run) => isRunActive(run.status));

  useEffect(() => {
    if (!anyActive) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [anyActive]);

  type Run = (typeof items)[number];
  const columns: TableColumn<Run>[] = [
    { id: 'mode', header: 'Art', track: '110px', cell: (run) => run.modeLabel },
    {
      id: 'status',
      header: 'Status',
      track: '140px',
      cell: (run) => <Pill variant={runStatusTones[run.status]}>{run.statusLabel}</Pill>,
    },
    {
      id: 'started',
      header: 'Gestartet',
      track: '150px',
      cell: (run) => dateTimeLabel(run.startedAt ?? run.createdAt),
    },
    { id: 'duration', header: 'Dauer', track: '100px', cell: (run) => durationLabel(run, now) },
    { id: 'by', header: 'Von', track: '150px', cell: (run) => startedByLabel(run) },
    {
      id: 'result',
      header: 'Ergebnis',
      cell: (run) =>
        isRunActive(run.status)
          ? (progressLine(run.progress) ?? 'läuft …')
          : run.error
            ? run.error
            : totalsLine(run.totals),
    },
  ];

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.cardHead}>
        <div className={styles.cardTitleBlock}>
          <span className={styles.eyebrow}>Läufe</span>
          <h3 id={titleId} className={styles.cardTitle}>
            Probeläufe und Importe
          </h3>
        </div>
      </div>
      {runs.isError ? <QueryError error={runs.error} /> : null}
      {runs.isPending ? <Skeleton variant="block" height={120} /> : null}
      {runs.isSuccess ? (
        <Table
          label="Läufe"
          columns={columns}
          rows={items}
          rowKey={(run) => run.id}
          onRowClick={(run) => void navigate(`/settings/organization/integrations/runs/${run.id}`)}
          emptyText="Noch kein Lauf. Starten Sie zuerst einen Probelauf."
        />
      ) : null}
    </section>
  );
}
